// ============================================================================
// LoadingSpinner Component
// ============================================================================
// Reusable loading spinner for API data fetching states
// Supports multiple sizes and optional loading text
// ============================================================================

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  text?: string;
  fullScreen?: boolean;
}

/**
 * LoadingSpinner Component
 *
 * @param size - Size of the spinner (sm, md, lg) - default: md
 * @param text - Optional loading text to display
 * @param fullScreen - Whether to center in full screen
 * @returns Loading spinner component
 *
 * @example
 * ```tsx
 * <LoadingSpinner size="lg" text="Loading products..." />
 * ```
 */
export function LoadingSpinner({
  size = 'md',
  text,
  fullScreen = false,
}: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: 'h-4 w-4 border-2',
    md: 'h-8 w-8 border-2',
    lg: 'h-12 w-12 border-4',
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  const containerClasses = fullScreen
    ? 'flex flex-col items-center justify-center min-h-screen gap-3'
    : 'flex flex-col items-center justify-center p-8 gap-3';

  return (
    <div className={containerClasses}>
      <div
        className={`animate-spin rounded-full border-indigo-600 border-t-transparent ${sizeClasses[size]}`}
        role="status"
        aria-label="Loading"
      />
      {text && (
        <p className={`text-gray-600 ${textSizeClasses[size]}`}>{text}</p>
      )}
    </div>
  );
}

/**
 * Inline LoadingSpinner (no padding)
 * Useful for inline loading states in tables, cards, etc.
 */
export function InlineLoadingSpinner({ size = 'sm' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizeClasses = {
    sm: 'h-4 w-4 border-2',
    md: 'h-6 w-6 border-2',
    lg: 'h-8 w-8 border-3',
  };

  return (
    <div
      className={`animate-spin rounded-full border-indigo-600 border-t-transparent ${sizeClasses[size]}`}
      role="status"
      aria-label="Loading"
    />
  );
}

export default LoadingSpinner;
